import { useEffect, useState, type ReactElement } from 'react';
import {
  GOOGLE_ICS_EXPORT_URL,
  GoogleNotConfiguredError,
  GoogleNotConnectedError,
  GoogleRateLimitedError,
  GoogleUnauthorizedError,
  GoogleUpstreamError,
  startGoogleOAuth,
  useDeleteGoogleAppConfig,
  useDisconnectGoogle,
  useGoogleAppConfig,
  useGoogleStatus,
  usePushGoogle,
  type GoogleAppConfig,
  type GoogleAppConfigPutResponse,
  type GooglePushResponse,
  type GoogleStatus,
} from '@/lib/google';
import { ConfirmDialog } from './ConfirmDialog';
import { GoogleAppConfigForm } from './GoogleAppConfigForm';

/**
 * Página `/ajustes/google` — tres estados según `status.appConfigured` y
 * `status.connected`:
 *
 *  1. `!appConfigured`               → <GoogleAppConfigForm /> (claves OAuth).
 *  2. `appConfigured && !connected`  → tarjeta config + CTA "Conectar Google".
 *  3. `appConfigured && connected`   → tarjeta config + cuenta + push/disconnect.
 *
 * Lee query params al cargar (`?ok=1` ó `?error=...`) para mostrar el
 * resultado del callback OAuth, y los limpia con `replaceState` para que un
 * F5 no muestre el banner otra vez.
 *
 * El botón "Descargar ICS" se renderiza SIEMPRE (estado 1/2/3) como
 * alternativa sin OAuth — el endpoint sirve los próximos 30 días de
 * planificación independientemente de si hay calendar conectado.
 */
export function GoogleSettings(): ReactElement {
  const statusQuery = useGoogleStatus();
  const appConfigQuery = useGoogleAppConfig();
  const pushMutation = usePushGoogle();
  const disconnectMutation = useDisconnectGoogle();
  const deleteAppConfigMutation = useDeleteGoogleAppConfig();

  const [callbackBanner, setCallbackBanner] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);
  const [savedBanner, setSavedBanner] = useState<
    { kind: 'success' | 'warning'; message: string } | null
  >(null);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [confirmDeleteConfigOpen, setConfirmDeleteConfigOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);

  // Lee y limpia los query params del callback OAuth una sola vez.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const ok = url.searchParams.get('ok');
    const errorCode = url.searchParams.get('error');
    if (!ok && !errorCode) return;

    if (ok === '1') {
      setCallbackBanner({ kind: 'success', message: 'Conectado correctamente con Google.' });
    } else if (errorCode) {
      setCallbackBanner({ kind: 'error', message: mapOAuthError(errorCode) });
    }

    url.searchParams.delete('ok');
    url.searchParams.delete('error');
    const cleaned = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState(window.history.state, '', cleaned);
  }, []);

  function handleConnect() {
    startGoogleOAuth();
  }

  function handlePush() {
    pushMutation.mutate(undefined);
  }

  function handleDisconnect() {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => setConfirmDisconnectOpen(false),
    });
  }

  function handleDeleteAppConfig() {
    deleteAppConfigMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteConfigOpen(false);
        setEditingConfig(false);
        setSavedBanner({
          kind: 'success',
          message: 'Configuración de la app Google borrada.',
        });
      },
    });
  }

  const status: GoogleStatus | undefined = statusQuery.data;
  const appConfig: GoogleAppConfig | undefined = appConfigQuery.data;

  const appConfigured: boolean =
    status?.appConfigured ?? appConfig?.configured ?? false;
  const connected: boolean = status?.connected ?? false;

  return (
    <section aria-labelledby="google-title" className="space-y-6">
      <header>
        <h1 id="google-title" className="text-2xl font-semibold tracking-tight text-slate-900">
          Google Calendar
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Sincroniza tus entrenamientos planificados con tu Google Calendar, o
          descarga un archivo .ics para importarlo en cualquier calendario.
        </p>
      </header>

      {callbackBanner && (
        <div
          role={callbackBanner.kind === 'success' ? 'status' : 'alert'}
          className={
            callbackBanner.kind === 'success'
              ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900'
              : 'rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800'
          }
        >
          {callbackBanner.message}
        </div>
      )}

      {savedBanner && (
        <div
          role="status"
          className={
            savedBanner.kind === 'success'
              ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900'
              : 'rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900'
          }
        >
          {savedBanner.message}
        </div>
      )}

      {(statusQuery.isLoading || appConfigQuery.isLoading) && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando estado de Google…
        </p>
      )}

      {statusQuery.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar el estado de Google.{' '}
          {statusQuery.error?.message ?? ''}
        </p>
      )}

      {appConfigQuery.isError && !statusQuery.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar la configuración de la app Google.{' '}
          {appConfigQuery.error?.message ?? ''}
        </p>
      )}

      {/* Estado 1: aún no hay claves configuradas → form directo. */}
      {status && appConfig && !appConfigured && (
        <GoogleAppConfigForm
          callbackUrl={appConfig.callbackUrl}
          mode="create"
          onSaved={(response) => {
            setSavedBanner({
              kind: 'success',
              message:
                'Claves guardadas. Ahora conecta tu cuenta de Google más abajo.',
            });
            if (response.previousConnectionInvalidated) {
              setSavedBanner({
                kind: 'warning',
                message:
                  'Tu conexión anterior se ha invalidado. Vuelve a conectar Google.',
              });
            }
          }}
        />
      )}

      {/* Estado 2 y 3: hay claves. Pintamos tarjeta de config (siempre) y luego
          la tarjeta de conexión apropiada. */}
      {status && appConfig && appConfigured && (
        <>
          <AppConfigCard
            appConfig={appConfig}
            editing={editingConfig}
            onEdit={() => setEditingConfig(true)}
            onCancelEdit={() => setEditingConfig(false)}
            onAskDelete={() => setConfirmDeleteConfigOpen(true)}
            onSaved={(response) => {
              setEditingConfig(false);
              if (response.previousConnectionInvalidated) {
                setSavedBanner({
                  kind: 'warning',
                  message:
                    'Claves actualizadas. Tu conexión anterior se ha invalidado: vuelve a conectar Google.',
                });
              } else {
                setSavedBanner({
                  kind: 'success',
                  message: 'Claves actualizadas correctamente.',
                });
              }
            }}
          />

          {connected ? (
            <ConnectedCard
              status={status}
              onPush={handlePush}
              onAskDisconnect={() => setConfirmDisconnectOpen(true)}
              pushIsPending={pushMutation.isPending}
              pushResult={pushMutation.data ?? null}
              pushError={pushMutation.error}
              onReconnect={handleConnect}
            />
          ) : (
            <ConnectAccountCard onConnect={handleConnect} />
          )}
        </>
      )}

      {/* Botón ICS — disponible en todos los estados, incluso sin app config */}
      <IcsExportCard />

      <ConfirmDialog
        open={confirmDisconnectOpen}
        title="¿Desconectar Google?"
        description={
          <>
            Los eventos ya creados en tu Google Calendar se conservan, pero no
            se actualizarán cuando cambies el plan. Esto no borra tus claves
            de la app ni los próximos entrenamientos planificados.
          </>
        }
        confirmLabel={disconnectMutation.isPending ? 'Desconectando…' : 'Desconectar'}
        destructive
        isPending={disconnectMutation.isPending}
        onConfirm={handleDisconnect}
        onCancel={() => setConfirmDisconnectOpen(false)}
      />

      <ConfirmDialog
        open={confirmDeleteConfigOpen}
        title="¿Borrar configuración de la app?"
        description={
          <>
            Se borrarán tus claves Client ID y Client Secret. Si estabas
            conectado, también se cerrará la sesión OAuth. Tendrás que
            volver a configurar la app para sincronizar.
          </>
        }
        confirmLabel={deleteAppConfigMutation.isPending ? 'Borrando…' : 'Borrar config'}
        destructive
        isPending={deleteAppConfigMutation.isPending}
        onConfirm={handleDeleteAppConfig}
        onCancel={() => setConfirmDeleteConfigOpen(false)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: configuración de la app (Client ID + botones editar/borrar)
// ---------------------------------------------------------------------------

function AppConfigCard({
  appConfig,
  editing,
  onEdit,
  onCancelEdit,
  onAskDelete,
  onSaved,
}: {
  appConfig: GoogleAppConfig;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onAskDelete: () => void;
  onSaved: (response: GoogleAppConfigPutResponse) => void;
}): ReactElement {
  if (editing) {
    return (
      <GoogleAppConfigForm
        callbackUrl={appConfig.callbackUrl}
        initialClientId={appConfig.clientId ?? ''}
        mode="edit"
        onSaved={onSaved}
        onCancel={onCancelEdit}
      />
    );
  }

  return (
    <section
      aria-labelledby="google-app-config-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id="google-app-config-title"
        className="text-lg font-semibold text-slate-900"
      >
        Configuración de la app
      </h2>
      <dl className="mt-3 space-y-2 text-sm">
        <Row label="Client ID">
          <code className="break-all rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">
            {appConfig.clientId ?? '—'}
          </code>
        </Row>
        {appConfig.updatedAt && (
          <Row label="Actualizado">
            <span className="text-slate-900">
              {formatDate(appConfig.updatedAt)}
            </span>
          </Row>
        )}
      </dl>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Editar claves
        </button>
        <button
          type="button"
          onClick={onAskDelete}
          className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Borrar config
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: conectar cuenta (estado 2 — app configurada pero sin OAuth)
// ---------------------------------------------------------------------------

function ConnectAccountCard({ onConnect }: { onConnect: () => void }): ReactElement {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">Conecta tu cuenta</h2>
      <p className="mt-2 text-sm text-slate-700">
        Conectar Google permite a tri-mentor crear y actualizar eventos en un
        calendario propio llamado <strong>tri-mentor</strong>. Pedimos los
        permisos:
      </p>
      <ul className="mt-2 list-disc pl-6 text-sm text-slate-700">
        <li>
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
            https://www.googleapis.com/auth/calendar.events
          </code>{' '}
          — crear, modificar y borrar eventos.
        </li>
      </ul>
      <p className="mt-2 text-sm text-slate-600">
        No leemos otros calendarios y nunca compartimos datos con terceros.
        Puedes desconectar en cualquier momento desde esta misma pantalla.
      </p>

      <div className="mt-6">
        <button
          type="button"
          onClick={onConnect}
          className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
        >
          Conectar Google
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: conectado (estado 3)
// ---------------------------------------------------------------------------

function ConnectedCard({
  status,
  onPush,
  onAskDisconnect,
  pushIsPending,
  pushResult,
  pushError,
  onReconnect,
}: {
  status: GoogleStatus;
  onPush: () => void;
  onAskDisconnect: () => void;
  pushIsPending: boolean;
  pushResult: GooglePushResponse | null;
  pushError: Error | null;
  onReconnect: () => void;
}): ReactElement {
  const expired = pushError instanceof GoogleUnauthorizedError;
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">Google conectado</h2>
      <dl className="mt-3 space-y-2 text-sm">
        <Row label="Cuenta">
          <span className="text-slate-900">
            {status.accountEmail ?? 'Sin email'}
          </span>
        </Row>
        <Row label="Último push">
          <span className="text-slate-900">
            {formatLastPush(status.lastPushAt ?? null)}
            {typeof status.lastPushCount === 'number' && status.lastPushAt && (
              <span className="ml-2 text-xs text-slate-500">
                ({status.lastPushCount} evento{status.lastPushCount === 1 ? '' : 's'})
              </span>
            )}
          </span>
        </Row>
        {status.calendarId && (
          <Row label="Calendario">
            <a
              href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(status.calendarId)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-700 underline hover:text-teal-800"
            >
              Abrir en Google Calendar
            </a>
          </Row>
        )}
      </dl>

      {pushResult && !pushError && (
        <p
          role="status"
          aria-live="polite"
          className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
        >
          Sincronización completada: {pushResult.created} creado
          {pushResult.created === 1 ? '' : 's'}, {pushResult.updated}{' '}
          actualizado{pushResult.updated === 1 ? '' : 's'}, {pushResult.deleted}{' '}
          eliminado{pushResult.deleted === 1 ? '' : 's'}.
        </p>
      )}

      {pushError && (
        <p role="alert" className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {mapPushError(pushError)}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {expired ? (
          <button
            type="button"
            onClick={onReconnect}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
          >
            Reconectar Google
          </button>
        ) : (
          <button
            type="button"
            onClick={onPush}
            disabled={pushIsPending}
            aria-busy={pushIsPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pushIsPending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner /> Sincronizando…
              </span>
            ) : (
              'Sincronizar ahora'
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onAskDisconnect}
          className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Desconectar
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: descarga ICS — alternativa sin OAuth, disponible siempre
// ---------------------------------------------------------------------------

function IcsExportCard(): ReactElement {
  return (
    <section
      aria-labelledby="google-ics-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2 id="google-ics-title" className="text-lg font-semibold text-slate-900">
        Exportar a .ics
      </h2>
      <p className="mt-2 text-sm text-slate-700">
        Si prefieres no usar OAuth, descarga un archivo{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">.ics</code>{' '}
        con tus próximos 30 días planificados y arrástralo a Google Calendar,
        Apple Calendar, Outlook o cualquier otra app que entienda iCalendar.
      </p>
      <p className="mt-2 text-xs text-slate-500">
        El archivo es estático — si cambias el plan, vuelve a descargarlo. Si
        quieres actualización automática, usa la sincronización por OAuth.
      </p>
      <div className="mt-4">
        <a
          href={GOOGLE_ICS_EXPORT_URL}
          download="tri-mentor.ics"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          <span aria-hidden="true">↓</span>
          Descargar .ics
        </a>
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Spinner(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mapOAuthError(code: string): string {
  switch (code) {
    case 'missing_state':
      return 'Se perdió la sesión OAuth. Reinicia el flujo.';
    case 'invalid_state':
      return 'Estado OAuth corrupto.';
    case 'state_mismatch':
      return 'Mismatch de estado OAuth, posible CSRF.';
    case 'google_app_no_configurado':
      return 'Configura primero tus claves Google antes de conectar.';
    case 'access_denied':
      return 'Cancelaste el flujo OAuth en Google.';
    default:
      return `Error al conectar con Google: ${code}`;
  }
}

function mapPushError(err: Error): string {
  if (err instanceof GoogleUnauthorizedError) {
    return 'Conexión con Google expirada. Reconecta.';
  }
  if (err instanceof GoogleRateLimitedError) {
    return 'Google limitó las peticiones. Intenta en unos minutos.';
  }
  if (err instanceof GoogleUpstreamError) {
    return 'Error en Google, vuelve a intentarlo.';
  }
  if (err instanceof GoogleNotConfiguredError) {
    return `Google no está configurado. ${err.hint}`;
  }
  if (err instanceof GoogleNotConnectedError) {
    return 'Conecta tu cuenta de Google antes de sincronizar.';
  }
  return 'No se pudo sincronizar con Google. Inténtalo de nuevo.';
}

function formatLastPush(iso: string | null): string {
  if (!iso) return 'Nunca';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'Hace unos segundos';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `Hace ${diffMin} minuto${diffMin === 1 ? '' : 's'}`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} hora${diffHour === 1 ? '' : 's'}`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 30) return `Hace ${diffDay} día${diffDay === 1 ? '' : 's'}`;
  return new Date(iso).toLocaleString('es-ES');
}

function formatDate(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  return new Date(iso).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
